import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notification } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import ContactUsApi from "../../../api-services/contact-us-api.service";
import useBreakpoint from "../../../utils/useBreakpoint";
import BasicInfoStep, { BasicInfoValues } from "./components/BasicInfoStep";
import TripPreferenceStep, { TripPreferenceValues } from "./components/TripPreferenceStep";
import TravelDetailsStep, { TravelDetailsValues } from "./components/TravelDetailsStep";
import SuccessView from "./components/SuccessView";

// Mobile frames title the last step in title case; the desktop frames don't.
const STEPS: { step: number; title: string; heading: string; mobileHeading?: string }[] = [
  { step: 1, title: "Basic Information", heading: "How can we contact you?" },
  { step: 2, title: "Trip Preferences", heading: "Tell us about your ideal trip" },
  { step: 3, title: "Travel Details", heading: "Your travel details", mobileHeading: "Your Travel Details" },
];

const ICONS = "/iceholidays-assets/images/icons";

const contactUsApi = new ContactUsApi();

function MobileProgress({ currentStep }: { currentStep: number }) {
  return (
    <div
      className="customize-tour-form__progress-bars"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={STEPS.length}
      aria-valuenow={currentStep}
    >
      {STEPS.map(({ step }) => (
        <span
          key={step}
          className={`customize-tour-form__progress-bar${step <= currentStep ? " customize-tour-form__progress-bar--filled" : ""}`}
        />
      ))}
    </div>
  );
}

function ProgressIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="customize-tour-form__progress">
      {STEPS.map(({ step, title }, idx) => {
        const state = step < currentStep ? "done" : step === currentStep ? "active" : "pending";
        return (
          <React.Fragment key={step}>
            <div className={`customize-tour-form__step customize-tour-form__step--${state}`}>
              <span className="customize-tour-form__step-dot">{state === "done" ? <CheckOutlined /> : step}</span>
              <span className="customize-tour-form__step-title">{title}</span>
            </div>
            {idx < STEPS.length - 1 && <span className={`customize-tour-form__step-line customize-tour-form__step-line--${state}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function CustomizeTourFormPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useBreakpoint();

  const tourCode = searchParams.get("tour_code") || "";
  const destination = searchParams.get("destination") || "";
  const departureDate = searchParams.get("departure_date") || "";

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [basicInfo, setBasicInfo] = useState<BasicInfoValues | null>(null);
  const [tripPreference, setTripPreference] = useState<TripPreferenceValues | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep, submitted]);

  // The mobile chevron steps back through the form and only leaves the page from step 1.
  const handleMobileBack = () => {
    if (currentStep === 1) navigate(-1);
    else setCurrentStep(currentStep - 1);
  };

  const handleBasicInfoNext = (values: BasicInfoValues) => {
    setBasicInfo(values);
    setCurrentStep(2);
  };

  const handleTripPreferenceNext = (values: TripPreferenceValues) => {
    setTripPreference(values);
    setCurrentStep(3);
  };

  const handleTravelDetailsSubmit = (values: TravelDetailsValues) => {
    if (!basicInfo || !tripPreference) return;

    setSubmitting(true);

    const [minBudget, maxBudget] = values.budget;

    const payload = {
      designation: basicInfo.designation,
      name: basicInfo.name,
      email: basicInfo.email,
      phone: basicInfo.phone,
      destination,
      tour_code: tourCode,
      departure_date: values.travelDate || departureDate,
      min_budget: minBudget,
      max_budget: maxBudget,
      agent: values.agentName,
      no_of_adults: tripPreference.adult,
      no_of_children: tripPreference.children,
      no_of_infants: tripPreference.infant,
      interest_experiences: tripPreference.interests,
      customizations: tripPreference.customizations,
      other_customization: tripPreference.customizationOther,
      remark: values.message.trim() || undefined,
    };

    contactUsApi
      .submitCustomizeTourInquiry(payload)
      .then((success) => {
        if (success) setSubmitted(true);
      })
      .catch(() => {
        notification.error({ message: "An error occured while sending your trip request." });
      })
      .finally(() => setSubmitting(false));
  };

  if (submitted) {
    return (
      <div className="customize-tour-form customize-tour-form--success">
        <SuccessView />
      </div>
    );
  }

  const { heading, mobileHeading } = STEPS[currentStep - 1];

  return (
    <div className="customize-tour-form">
      <div className="customize-tour-form__bg">
        <div className="customize-tour-form__top">
          <div className="customize-tour-form__top-row">
            {isMobile ? (
              <>
                <button type="button" className="customize-tour-form__back-icon" aria-label="Back" onClick={handleMobileBack}>
                  <img src={`${ICONS}/icon-ctf-chevron-left.svg`} alt="" aria-hidden="true" />
                </button>

                <MobileProgress currentStep={currentStep} />
              </>
            ) : (
              <>
                <button type="button" className="customize-tour-form__back" onClick={() => navigate(-1)}>
                  <img src={`${ICONS}/icon-ctf-arrow-left.svg`} alt="" aria-hidden="true" />
                  Back
                </button>

                <ProgressIndicator currentStep={currentStep} />
              </>
            )}
          </div>

          <h2 className="customize-tour-form__heading">{isMobile && mobileHeading ? mobileHeading : heading}</h2>
        </div>

        <div className="customize-tour-form__card">
          {currentStep === 1 && <BasicInfoStep initialValues={basicInfo} onNext={handleBasicInfoNext} />}
          {currentStep === 2 && (
            <TripPreferenceStep
              initialValues={tripPreference}
              onBack={() => setCurrentStep(1)}
              onNext={handleTripPreferenceNext}
            />
          )}
          {currentStep === 3 && (
            <TravelDetailsStep
              initialDepartureDate={departureDate}
              submitting={submitting}
              onBack={() => setCurrentStep(2)}
              onSubmit={handleTravelDetailsSubmit}
            />
          )}
        </div>
      </div>
    </div>
  );
}
